import { getProductFacts } from "@/lib/product-facts";
import { guardClaims } from "@/lib/claim-guard";

/**
 * Product-specific benefits.
 *
 * Every bullet comes from the manufacturer's own "Ominaisuudet" section in the
 * canonical fact sheet (`featureList`). The previous version guessed a category
 * from the title/tags and rendered invented benefit copy — that put climbing and
 * vestibular claims on stacking toys. Nothing is generated here: when a product
 * has no verified bullets, no section is rendered.
 */
export function ProductBenefits({
  handle,
  age,
  features,
}: {
  handle?: string;
  /** Verified age recommendation. Omitted when the product has none on file. */
  age?: string;
  /** Verified benefit bullets; falls back to the fact sheet for `handle`. */
  features?: string[];
}) {
  const items = (features ?? getProductFacts(handle).featureList ?? []).map((f) => guardClaims(f));
  if (items.length === 0) return null;

  return (
    <section className="mt-8 rounded-2xl border border-border bg-cream/60 p-5 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay">
          <span aria-hidden>✦</span> Ominaisuudet ja hyödyt
        </div>
        {age && (
          <span className="rounded-full bg-foreground/5 px-2.5 py-1 text-[11px] font-medium text-foreground/80">
            {age}
          </span>
        )}
      </div>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-3 rounded-xl bg-background/60 p-3">
            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background">
              ✓
            </span>
            <p className="min-w-0 text-sm leading-snug text-foreground">{it}</p>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-muted-foreground">
        Tiedot valmistajan omasta tuotekuvauksesta.
      </p>
    </section>
  );
}

// NOTE: the old always-on spec table lived here. It filled every product with
// invented defaults (materials, weight limits, "Suunniteltu Suomessa"), which
// contradicted real supplier data. Structured specs now come from the canonical
// fact sheet — see `src/lib/product-facts.ts` and <ProductSpecSheet />.

/**
 * Review metadata contract. A review may only display the
 * "Vahvistettu ostos" badge when order records confirm the purchase, and must
 * display the incentive disclosure when the reviewer received any benefit.
 */
export type ReviewMeta = {
  verifiedPurchase?: boolean;
  incentivized?: boolean;
  incentiveDescription?: string;
};

export const INCENTIVE_DISCLOSURE =
  "Arvostelu annettu kampanjassa, jossa asiakkaalle tarjottiin alennuskoodi arvostelun jättämisestä riippumatta arvostelun sisällöstä.";

/** Small badges rendered next to an individual review. */
export function ReviewDisclosure({ meta }: { meta: ReviewMeta }) {
  if (!meta.verifiedPurchase && !meta.incentivized) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {meta.verifiedPurchase && (
        <span className="rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] font-medium text-foreground/80">
          Vahvistettu ostos
        </span>
      )}
      {meta.incentivized && (
        <span className="text-[10px] leading-snug text-muted-foreground">
          {meta.incentiveDescription ?? INCENTIVE_DISCLOSURE}
        </span>
      )}
    </div>
  );
}

/**
 * Reviews block. Renders nothing at all when there are no reviews — no star
 * summary, no "ei vielä arvosteluja" empty state. The system stays wired: as
 * soon as real reviews exist they render normally, with disclosures.
 */
export type ProductReview = ReviewMeta & {
  id: string;
  author: string;
  rating: number;
  body: string;
  date?: string;
};

export function ProductReviews({
  productTitle,
  reviews = [],
}: {
  productTitle: string;
  reviews?: ProductReview[];
}) {
  if (reviews.length === 0) return null;

  const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  const anyIncentivized = reviews.some((r) => r.incentivized);

  return (
    <section
      id="arvostelut"
      aria-labelledby="reviews-heading"
      className="mt-10 border-t border-border/70 pt-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="reviews-heading" className="text-sm font-medium text-foreground">
            Asiakasarvostelut
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {avg.toFixed(1)} / 5 · {reviews.length} arvostelua
          </p>
        </div>
        <a
          href={`mailto:asiakaspalvelu@montessori24.fi?subject=${encodeURIComponent(
            `Arvostelu: ${productTitle}`,
          )}`}
          className="inline-flex items-center justify-center rounded-full border border-border bg-background px-4 py-2 text-xs font-medium text-foreground transition hover:border-foreground"
        >
          Kirjoita arvostelu
        </a>
      </div>

      <ul className="mt-6 space-y-6">
        {reviews.map((r) => (
          <li key={r.id} className="border-b border-border/60 pb-5 last:border-b-0">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground">{r.author}</span>
              <span className="text-xs text-muted-foreground">{r.rating} / 5</span>
              {r.date ? <span className="text-xs text-muted-foreground">{r.date}</span> : null}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
            <ReviewDisclosure meta={r} />
          </li>
        ))}
      </ul>

      {anyIncentivized ? (
        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
          {INCENTIVE_DISCLOSURE}
        </p>
      ) : null}
    </section>
  );
}